import React, { useEffect, useState } from 'react';
import { container } from 'tsyringe';
import { IAuthService } from '@base/features/auth/model/interfaces';
import { getRequiredEnv, t } from '../../../../utils/helpers';
import { Checkbox } from '@ui-kit/components/checkbox';
import { Link, useHistory } from 'react-router-dom';
import { MAIN_ROUTES } from '../../../routing/constants/mainRoutes';
import MainButton from '@ui-kit/components/mainButton';
import { GuestEntrance } from '../guestEntrance';
import { Loader } from '@ui-kit/components/loader';
import { StorageKeys } from '../../../../utils/constants';
import { useForm, useController } from 'react-hook-form';
import { ILocalStorage } from '../../../storage/interfaces';
import { isUserAuthData } from '../../utils/typequards';
import SocialEntrance from '../socialEntrance';
import { useFacebookLogin } from '../../hooks/facebookLogin';
import getMessage from '../../utils/getMessage';
import { loginMessages } from '../../model/const';
import PasswordInput from '@ui-kit/components/passwordInput';
import { IModalProvider } from '@base/features/modal/model/interfaces';
import AuthWrapImage from '../authWrapImage';
import { PlayerStorageKeys } from '@base/features/player/model/interfaces';
import { IGameProvider } from '@base/features/game/model/interfaces';
import styles from './login.module.scss';

export default function Login() {
  const history = useHistory();
  const localStorage: ILocalStorage = container.resolve('localStorage');
  const gameProvider: IGameProvider = container.resolve('gameProvider');

  const [registerUsername] = useState(() => localStorage.get('remember_login'));

  const {
    formState: { dirtyFields, isSubmitting },
    handleSubmit,
    getValues,
    setValue,
    control,
    register,
  } = useForm({
    defaultValues: {
      usernameState: registerUsername,
      passwordState: '',
      rememberLogin: false,
    },
  });

  const { field: rememberInput } = useController({
    name: 'rememberLogin',
    control,
  });

  const modalProvider: IModalProvider = container.resolve('modalProvider');
  const authService: IAuthService = container.resolve('authService');

  const handleLoginExtension = handleSubmit(async (data) => {
    try {
      const response = await authService.doLoginDefault(data.usernameState, data.passwordState);
      if (isUserAuthData(response)) {
        response.proxy = getRequiredEnv('NX_EXT_PROXY');
        response.host = getRequiredEnv('NX_EXT_HOST');
        response.port = Number(getRequiredEnv('NX_EXT_PORT'));
        authService.doEntryDefault(response);
      }
    } catch (err) {
      console.error(err);
      modalProvider.show({
        title: t('error'),
        body: t('unknown_error_try_again'),
        buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
      });
    }
  });

  const handleLogin = handleSubmit(async (data) => {
    try {
      const response = await authService.doLoginDefault(data.usernameState, data.passwordState);
      if (isUserAuthData(response)) {
        if (response.Phone_Verified === 'Y' || response.Mail_Verified === 'Y') {
          authService.doEntryDefault(response);
        } else {
          localStorage.set(StorageKeys.TEMP_DATA, response);
          modalProvider.show({
            title: 'Info',
            body: t('you_need_verify_account'),
            buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
          });
          history.push(MAIN_ROUTES.verification);
        }
      } else {
        showLoginModal(response.code);
      }
    } catch (err) {
      console.error(err);
      modalProvider.show({
        title: t('error'),
        body: t('unknown_error_try_again'),
        buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
      });
    }
  });

  function showLoginModal(code: number): void {
    const msg = getMessage(code, loginMessages);
    modalProvider.show({
      title: t(msg.title),
      body: t(msg.message),
      buttons: [{ title: t('ok'), onClick: () => modalProvider.hide() }],
    });
  }
  const { state, login } = useFacebookLogin();

  useEffect(() => {
    if (process.env.NX_FACEBOOK_GAME && state.loaded) {
      login();
    }
  }, [state.loaded]);

  return (
    <div className={styles.auth}>
      {isSubmitting && <Loader className="loader-container--black" />}
      <AuthWrapImage />
      <div className={styles.loginPageWrap}>
        <div className={styles.authContentWrap}>
          <div className={styles.loginWrap}>
            <h1 className={styles.loginTitle}>{gameProvider.APP_NAME}</h1>
            <form>
              <div className="auth-form-item">
                <input
                  className="auth-form-item__text auth-form-item__text--dont-need-valid"
                  placeholder={t('username')}
                  id="login-username"
                  type="text"
                  {...register('usernameState')}
                  autoComplete={dirtyFields.rememberLogin ? 'on' : 'off'}
                />
              </div>
              <div className="auth-form-item">
                <PasswordInput
                  className="auth-form-item__text auth-form-item__text--dont-need-valid"
                  placeholder={t('password')}
                  id="login-password"
                  {...register('passwordState')}
                  autoComplete={dirtyFields.rememberLogin ? 'on' : 'off'}
                />
              </div>
              <div className={styles.loginRemember}>
                <Checkbox
                  classInput={styles.loginRememberInput}
                  classLabel={styles.loginRememberLabel}
                  id="login-remember"
                  {...rememberInput}
                  autocomplete={dirtyFields.rememberLogin ? 'on' : 'off'}
                  checked={dirtyFields.rememberLogin}
                  name="login-remember"
                  handler={() => setValue('rememberLogin', !getValues('rememberLogin'))}
                >
                  {t('remember_login')}
                </Checkbox>

                <Link to={MAIN_ROUTES.forgot} className={styles.loginForgot}>
                  {t('forgot_your_password')}
                </Link>
              </div>
              <div className={styles.loginBtnWrap}>
                <MainButton type="submit" className={styles.btnSubmit} onClick={handleLogin}>
                  {t('login')}
                </MainButton>
              </div>
              {
                <div className={styles.extension} onClick={handleLoginExtension}>
                  Extension hall
                </div>
              }
            </form>
            <SocialEntrance />
            <div className={styles.loginBtnWrap}>
              <MainButton className={styles.btnOpacity} onClick={() => history.push(MAIN_ROUTES.register)}>
                {t('registration')}
              </MainButton>
            </div>
            {localStorage.get(PlayerStorageKeys.GAMES_PLAYED) < 5 && <GuestEntrance />}
          </div>
        </div>
      </div>
    </div>
  );
}
